/**
 * Support Desk — list tickets (SON-######) - list tickets with filters
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Checkbox,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Radio,
  RadioGroup,
  FormControlLabel as MuiFormControlLabel,
  FormLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Clear as ClearIcon,
  DeleteOutline as DeleteOutlineIcon,
  MergeType as MergeTypeIcon,
} from '@mui/icons-material';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import SupportDeskTicketFormDialog from '../components/SupportDeskTicketFormDialog';

type OptionItem = { value: string; label: string; sort_order: number };

const SupportDeskPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState<{
    q: string;
    status: string;
    exclude_status?: string;
    category: string;
    assignee_id: string;
    created_after: string;
    created_before: string;
    sort_by: string;
    sort_order: string;
    exclude_merged: boolean;
  }>({
    q: '',
    status: '',
    exclude_status: 'closed',
    category: '',
    assignee_id: '',
    created_after: '',
    created_before: '',
    sort_by: 'last_activity_at',
    sort_order: 'desc',
    exclude_merged: true,
  });
  const [users, setUsers] = useState<{ id: string; username: string; display_name: string }[]>([]);
  const myUserId = users.find((u) => u.username === user?.username)?.id;
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkWorking, setBulkWorking] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeParentId, setMergeParentId] = useState<string>('');
  const [mergeComment, setMergeComment] = useState('');
  const [fieldOptions, setFieldOptions] = useState<{
    status: OptionItem[];
    priority: OptionItem[];
    category: OptionItem[];
    channel: OptionItem[];
  }>({ status: [], priority: [], category: [], channel: [] });

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, any> = {
        page: page + 1,
        per_page: rowsPerPage,
        sort_by: filters.sort_by || 'last_activity_at',
        sort_order: filters.sort_order || 'desc',
      };
      if (filters.q) params.q = filters.q;
      if (filters.status) params.status = filters.status;
      if (filters.exclude_status) params.exclude_status = filters.exclude_status;
      if (filters.category) params.category = filters.category;
      if (filters.assignee_id) params.assignee_id = filters.assignee_id;
      if (filters.created_after) params.created_after = filters.created_after;
      if (filters.created_before) params.created_before = filters.created_before;
      if (filters.exclude_merged) params.exclude_merged = true;
      const res = await apiService.getSupportDeskTickets(params);
      setTickets(res.tickets);
      setTotal(res.total);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    setSelected(new Set());
  }, [page, rowsPerPage]);

  useEffect(() => {
    apiService.getUsers().then(setUsers).catch(() => {});
  }, []);

  useEffect(() => {
    apiService
      .getSupportDeskFieldOptionsGrouped()
      .then((g) => {
        setFieldOptions({
          status: (g.status || []) as OptionItem[],
          priority: (g.priority || []) as OptionItem[],
          category: (g.category || []) as OptionItem[],
          channel: (g.channel || []) as OptionItem[],
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const s = searchParams.get('status');
    if (s) {
      setFilters((prev) => {
        if (prev.status === s && prev.exclude_status === undefined && prev.exclude_merged === false) return prev;
        return { ...prev, status: s, exclude_status: undefined, exclude_merged: false };
      });
      setPage(0);
    }
  }, [searchParams]);

  type ChipColor = 'default' | 'primary' | 'success' | 'warning' | 'error';
  const toChipColor = (c?: string | null): ChipColor =>
    (c && ['default', 'primary', 'success', 'warning', 'error'].includes(c) ? c : 'default') as ChipColor;
  const statusOptions = fieldOptions.status.length > 0 ? fieldOptions.status : [];
  const statusLabelMap: Record<string, string> = statusOptions.length > 0
    ? Object.fromEntries(statusOptions.map((o) => [o.value, o.label]))
    : {};
  const statusColorMap: Record<string, ChipColor> = statusOptions.length > 0
    ? Object.fromEntries(statusOptions.map((o) => [o.value, toChipColor((o as { color?: string }).color)]))
    : {};
  const priorityLabelMap: Record<string, string> = fieldOptions.priority.length > 0
    ? Object.fromEntries(fieldOptions.priority.map((o) => [o.value, o.label]))
    : {};
  const priorityColorMap: Record<string, ChipColor> = fieldOptions.priority.length > 0
    ? Object.fromEntries(fieldOptions.priority.map((o) => [o.value, toChipColor((o as { color?: string }).color)]))
    : {};

  const categoryLabelMap: Record<string, string> =
    fieldOptions.category && fieldOptions.category.length > 0
      ? Object.fromEntries(fieldOptions.category.map((o) => [o.value, o.label]))
      : {};

  const handleClearFilters = () => {
    setFilters({
      ...filters,
      q: '',
      status: '',
      exclude_status: 'closed',
      category: '',
      assignee_id: '',
      created_after: '',
      created_before: '',
      exclude_merged: true,
    });
    setSearchParams((sp) => {
      const n = new URLSearchParams(sp);
      n.delete('status');
      return n;
    }, { replace: true });
    setPage(0);
  };

  const handleSort = (field: string) => {
    const isAsc = filters.sort_by === field && filters.sort_order === 'asc';
    setFilters({ ...filters, sort_by: field, sort_order: isAsc ? 'desc' : 'asc' });
    setPage(0);
  };

  const handleTicketCreated = () => {
    setFormOpen(false);
    fetchTickets();
  };

  const toggleSelected = (id: string) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const selectableTicketsOnPage = tickets.filter((t) => t.status !== 'merged');

  const toggleSelectAllPage = () => {
    if (selectableTicketsOnPage.length === 0) return;
    const allOnPage = selectableTicketsOnPage.every((t) => selected.has(t.id));
    setSelected((prev) => {
      const n = new Set(prev);
      if (allOnPage) {
        selectableTicketsOnPage.forEach((t) => n.delete(t.id));
      } else {
        selectableTicketsOnPage.forEach((t) => n.add(t.id));
      }
      return n;
    });
  };

  const selectedIds = Array.from(selected);

  const applyBulkStatus = async () => {
    if (!bulkStatus || selectedIds.length === 0) return;
    setBulkWorking(true);
    setError(null);
    try {
      await apiService.bulkSupportDeskTicketsStatus(selectedIds, bulkStatus);
      setSelected(new Set());
      await fetchTickets();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to update tickets');
    } finally {
      setBulkWorking(false);
    }
  };

  const openMergeDialog = () => {
    const cand = tickets
      .filter((t) => selected.has(t.id) && t.status !== 'merged')
      .sort((a, b) => a.ticket_number.localeCompare(b.ticket_number, undefined, { numeric: true }));
    if (cand.length < 2) return;
    setMergeParentId(cand[0].id);
    setMergeComment('');
    setMergeOpen(true);
  };

  const confirmMerge = async () => {
    if (!mergeParentId || selectedIds.length < 2) return;
    setBulkWorking(true);
    setError(null);
    try {
      const parent = await apiService.mergeSupportDeskTickets({
        ticket_ids: selectedIds,
        parent_ticket_id: mergeParentId,
        comment: mergeComment.trim() || undefined,
      });
      setMergeOpen(false);
      setSelected(new Set());
      await fetchTickets();
      navigate(`/crm/support-desk/${parent.id}`);
    } catch (e: any) {
      setError(
        typeof e?.response?.data?.detail === 'string'
          ? e.response.data.detail
          : 'Failed to merge tickets'
      );
    } finally {
      setBulkWorking(false);
    }
  };

  const confirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkWorking(true);
    setError(null);
    try {
      await apiService.bulkSupportDeskTicketsDelete(selectedIds);
      setDeleteConfirmOpen(false);
      setSelected(new Set());
      await fetchTickets();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to delete tickets');
    } finally {
      setBulkWorking(false);
    }
  };

  const formatDate = (s: string | null | undefined) => {
    if (!s) return '-';
    return new Date(s).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  /** Ticket created_at — for email-ingested tickets this is when the message was recorded. */
  const formatDateTime = (s: string | null | undefined) => {
    if (!s) return '—';
    return new Date(s).toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h1" fontWeight={600}>
          Support Desk
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setFormOpen(true)}
        >
          Add ticket
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search subject, ticket #, description…"
            value={filters.q || ''}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={!filters.status && filters.exclude_status === 'closed' ? '__exclude_closed__' : (filters.status || '')}
              label="Status"
              onChange={(e) => {
                const v = e.target.value;
                if (v === '__exclude_closed__') {
                  setFilters({ ...filters, status: '', exclude_status: 'closed', exclude_merged: true });
                  setSearchParams((sp) => {
                    const n = new URLSearchParams(sp);
                    n.delete('status');
                    return n;
                  }, { replace: true });
                } else if (v) {
                  setFilters({ ...filters, status: v, exclude_status: undefined, exclude_merged: false });
                  setSearchParams((sp) => {
                    const n = new URLSearchParams(sp);
                    n.set('status', v);
                    return n;
                  }, { replace: true });
                } else {
                  setFilters({ ...filters, status: '', exclude_status: undefined, exclude_merged: false });
                  setSearchParams((sp) => {
                    const n = new URLSearchParams(sp);
                    n.delete('status');
                    return n;
                  }, { replace: true });
                }
                setPage(0);
              }}
            >
              <MenuItem value="__exclude_closed__">All except closed</MenuItem>
              <MenuItem value="">All</MenuItem>
              {statusOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={filters.category || ''}
              label="Category"
              onChange={(e) => {
                setFilters({ ...filters, category: e.target.value || '' });
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {(fieldOptions.category || []).map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Assignee</InputLabel>
            <Select
              value={filters.assignee_id || ''}
              label="Assignee"
              onChange={(e) => {
                setFilters({ ...filters, assignee_id: e.target.value || '' });
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.display_name || u.username}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="date"
            label="Created from"
            InputLabelProps={{ shrink: true }}
            value={filters.created_after}
            onChange={(e) => {
              setFilters({ ...filters, created_after: e.target.value });
              setPage(0);
            }}
            sx={{ minWidth: 155 }}
          />
          <TextField
            size="small"
            type="date"
            label="Created to"
            InputLabelProps={{ shrink: true }}
            value={filters.created_before}
            onChange={(e) => {
              setFilters({ ...filters, created_before: e.target.value });
              setPage(0);
            }}
            sx={{ minWidth: 155 }}
          />
          <Button size="small" startIcon={<ClearIcon />} onClick={handleClearFilters}>
            Clear
          </Button>
          <Button variant="contained" onClick={fetchTickets} disabled={loading}>
            Apply
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {selectedIds.length > 0 && (
        <Paper sx={{ mb: 2 }}>
          <Toolbar variant="dense" sx={{ gap: 2, flexWrap: 'wrap', py: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              {selectedIds.length} selected
            </Typography>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Set status</InputLabel>
              <Select
                value={bulkStatus}
                label="Set status"
                onChange={(e) => setBulkStatus(e.target.value)}
              >
                <MenuItem value="">
                  <em>Choose status…</em>
                </MenuItem>
                {statusOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              disabled={!bulkStatus || bulkWorking}
              onClick={applyBulkStatus}
            >
              Apply status
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<MergeTypeIcon />}
              disabled={
                bulkWorking ||
                tickets.filter((t) => selected.has(t.id) && t.status !== 'merged').length < 2
              }
              onClick={openMergeDialog}
            >
              Merge…
            </Button>
            <Button
              color="error"
              variant="outlined"
              size="small"
              startIcon={<DeleteOutlineIcon />}
              disabled={bulkWorking}
              onClick={() => setDeleteConfirmOpen(true)}
            >
              Delete
            </Button>
            <Button size="small" onClick={() => setSelected(new Set())}>
              Clear selection
            </Button>
          </Toolbar>
        </Paper>
      )}

      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        indeterminate={
                          selectableTicketsOnPage.some((t) => selected.has(t.id)) &&
                          !selectableTicketsOnPage.every((t) => selected.has(t.id))
                        }
                        checked={
                          selectableTicketsOnPage.length > 0 &&
                          selectableTicketsOnPage.every((t) => selected.has(t.id))
                        }
                        onChange={toggleSelectAllPage}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: 'nowrap',
                        minWidth: '8.5rem',
                        width: '1%',
                        verticalAlign: 'middle',
                      }}
                    >
                      <TableSortLabel
                        active={filters.sort_by === 'ticket_number'}
                        direction={filters.sort_by === 'ticket_number' ? (filters.sort_order as 'asc' | 'desc') : 'asc'}
                        onClick={() => handleSort('ticket_number')}
                      >
                        Ticket #
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="When the ticket was created. For emails from the support mailbox, this is when the message was imported.">
                        <TableSortLabel
                          active={filters.sort_by === 'created_at'}
                          direction={filters.sort_by === 'created_at' ? (filters.sort_order as 'asc' | 'desc') : 'asc'}
                          onClick={() => handleSort('created_at')}
                        >
                          Created
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Newest reply, note or update on the ticket (or when it was created)">
                        <TableSortLabel
                          active={filters.sort_by === 'last_activity_at'}
                          direction={filters.sort_by === 'last_activity_at' ? (filters.sort_order as 'asc' | 'desc') : 'asc'}
                          onClick={() => handleSort('last_activity_at')}
                        >
                          Last updated
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={filters.sort_by === 'subject'}
                        direction={filters.sort_by === 'subject' ? (filters.sort_order as 'asc' | 'desc') : 'asc'}
                        onClick={() => handleSort('subject')}
                      >
                        Subject
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={filters.sort_by === 'category'}
                        direction={filters.sort_by === 'category' ? (filters.sort_order as 'asc' | 'desc') : 'asc'}
                        onClick={() => handleSort('category')}
                      >
                        Category
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={filters.sort_by === 'status'}
                        direction={filters.sort_by === 'status' ? (filters.sort_order as 'asc' | 'desc') : 'asc'}
                        onClick={() => handleSort('status')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={filters.sort_by === 'priority'}
                        direction={filters.sort_by === 'priority' ? (filters.sort_order as 'asc' | 'desc') : 'asc'}
                        onClick={() => handleSort('priority')}
                      >
                        Priority
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Assignee</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={filters.sort_by === 'due_resolve_at'}
                        direction={filters.sort_by === 'due_resolve_at' ? (filters.sort_order as 'asc' | 'desc') : 'asc'}
                        onClick={() => handleSort('due_resolve_at')}
                      >
                        Resolve
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tickets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                        No tickets found
                      </TableCell>
                    </TableRow>
                  ) : (
                    tickets.map((c) => (
                      <TableRow
                        key={c.id}
                        hover
                        selected={selected.has(c.id)}
                        sx={
                          c.has_unseen_update && c.last_update_by_id !== myUserId
                            ? { bgcolor: 'rgba(66, 165, 245, 0.18)', '&:hover': { bgcolor: 'rgba(66, 165, 245, 0.28)' } }
                            : undefined
                        }
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={selected.has(c.id)}
                            disabled={c.status === 'merged'}
                            onChange={() => toggleSelected(c.id)}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            whiteSpace: 'nowrap',
                            minWidth: '8.5rem',
                            width: '1%',
                            verticalAlign: 'middle',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
                            <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                              {c.ticket_number}
                            </Box>
                            {c.merged_into_ticket_number && (
                              <Chip
                                size="small"
                                label={`→ ${c.merged_into_ticket_number}`}
                                variant="outlined"
                                sx={{ flexShrink: 0 }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', typography: 'body2' }}>{formatDateTime(c.created_at)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', typography: 'body2' }}>
                          {formatDateTime(c.last_activity_at || c.created_at)}
                        </TableCell>
                        <TableCell>{c.subject}</TableCell>
                        <TableCell>{c.account_display_name || c.account_id || '—'}</TableCell>
                        <TableCell>{categoryLabelMap[c.category || ''] || c.category || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            label={statusLabelMap[c.status] || c.status}
                            size="small"
                            color={statusColorMap[c.status] ?? 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={priorityLabelMap[c.priority] || c.priority}
                            size="small"
                            color={priorityColorMap[c.priority] ?? 'default'}
                          />
                        </TableCell>
                        <TableCell>{c.assignee_name || '-'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            {formatDate(c.due_resolve_at)}
                            {c.due_resolve_at && c.status !== 'closed' && c.status !== 'resolved' && new Date(c.due_resolve_at) < new Date() && (
                              <Chip label="Overdue" size="small" color="error" variant="outlined" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => navigate(`/crm/support-desk/${c.id}`)}>
                            <ViewIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100, 200]}
            />
          </>
        )}
      </Paper>

      <SupportDeskTicketFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={handleTicketCreated}
        accountId={undefined}
      />

      <Dialog open={mergeOpen} onClose={() => !bulkWorking && setMergeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Merge tickets</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Choose the <strong>primary ticket</strong> (parent). All other selected tickets will be closed as{' '}
            <strong>Merged</strong>, their attachments will move to the parent, and inbound email referencing old
            ticket numbers will be routed to the parent. Active workflows and MSP hour reservations must not
            conflict—see the error message if merge is blocked.
          </DialogContentText>
          <FormLabel component="legend">Primary ticket</FormLabel>
          <RadioGroup
            value={mergeParentId}
            onChange={(e) => setMergeParentId(e.target.value)}
            sx={{ mt: 1 }}
          >
            {tickets
              .filter((t) => selected.has(t.id) && t.status !== 'merged')
              .sort((a, b) => a.ticket_number.localeCompare(b.ticket_number, undefined, { numeric: true }))
              .map((t) => (
                <MuiFormControlLabel
                  key={t.id}
                  value={t.id}
                  control={<Radio size="small" />}
                  label={
                    <Typography variant="body2" component="span">
                      <strong>{t.ticket_number}</strong> — {t.subject || '(No subject)'}
                    </Typography>
                  }
                />
              ))}
          </RadioGroup>
          <TextField
            label="Internal comment (optional)"
            value={mergeComment}
            onChange={(e) => setMergeComment(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 2 }}
            placeholder="Why these tickets were merged…"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeOpen(false)} disabled={bulkWorking}>
            Cancel
          </Button>
          <Button variant="contained" onClick={confirmMerge} disabled={!mergeParentId || bulkWorking}>
            {bulkWorking ? 'Merging…' : 'Merge tickets'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => !bulkWorking && setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete tickets?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete {selectedIds.length} ticket
            {selectedIds.length === 1 ? '' : 's'}? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={bulkWorking}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={confirmBulkDelete} disabled={bulkWorking}>
            {bulkWorking ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportDeskPage;
